"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, User } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <div className="h-12 bg-muted-light/30 rounded-md animate-pulse mb-6" />
        <div className="h-96 bg-muted-light/30 rounded-lg animate-pulse mb-8" />
        <div className="space-y-4">
          <div className="h-4 bg-muted-light/30 rounded animate-pulse" />
          <div className="h-4 bg-muted-light/30 rounded animate-pulse w-5/6" />
          <div className="h-4 bg-muted-light/30 rounded animate-pulse w-4/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <p className="text-muted-dark text-center">Failed to load post.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <p className="text-muted-dark text-center">Post not found.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto px-4 py-16 max-w-4xl">
      <header className="mb-8">
        <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-6">
          {post.title}
        </h1>

        <div className="flex items-center gap-6 text-sm text-muted-dark">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="size-4" />
              <span>{post.author.name}</span>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-12 rounded-lg overflow-hidden shadow-medium">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      {post.content && (
        <div
          className="prose prose-lg max-w-none mb-12 text-foreground-light"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {post.author && (
        <>
          <Separator className="my-12" />
          <div className="flex items-center gap-4 p-6 bg-primary-light/20 rounded-lg">
            <Avatar className="size-16">
              {post.author.avatar && post.author.avatar["96"] ? (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              ) : null}
              <AvatarFallback className="bg-primary text-foreground-primary text-xl">
                {post.author.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-display font-semibold text-lg text-foreground">
                {post.author.name}
              </p>
              {post.author.link && (
                <Link
                  to={post.author.link}
                  className="text-sm text-primary hover:text-primary-hover transition-colors duration-[var(--duration-medium)]"
                >
                  View all posts by {post.author.name}
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </article>
  );
}

function RelatedPostsSection() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <section className="bg-primary-light/10 py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-display font-bold text-foreground mb-8">
            Related Posts
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} data-index={i} className="h-64 bg-muted-light/30 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <section className="bg-primary-light/10 py-16">
      <div className="container mx-auto px-4 max-w-6xl">
        <h2 className="text-3xl font-display font-bold text-foreground mb-8">
          Related Posts
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <Card
              key={post.id}
              data-index={i}
              className="border border-border hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)]"
            >
              {post.featuredImage && (
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-48 object-cover rounded-t-xl"
                />
              )}
              <CardHeader>
                <CardTitle className="text-lg font-display text-foreground">
                  {post.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {post.excerpt && (
                  <div
                    className="text-sm text-muted-dark mb-4 line-clamp-3"
                    dangerouslySetInnerHTML={{ __html: post.excerpt }}
                  />
                )}
                {post.link && (
                  <Link
                    to={post.link}
                    className="inline-flex items-center text-sm font-semibold text-primary hover:text-primary-hover transition-colors duration-[var(--duration-medium)]"
                  >
                    Read More →
                  </Link>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function SinglePostTemplate() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <>
      <PostsProvider wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "author"]} fields={["id", "title.rendered", "content.rendered", "date", "author", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>

      <PostsProvider wp_query={{ exclude: [currentPostId], per_page: 3, order: "desc", orderby: "date" }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "_embedded", "featured_media", "_links.wp:featuredmedia"]}>
        <RelatedPostsSection />
      </PostsProvider>
    </>
  );
}